import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ErrorProvider,
  MfaErrorProvider,
  MfaSetupContext,
  ProfileContext,
  UserContext,
} from "@app/provider";
import { Factor } from "@app/service/user";
import { useErrorController } from "@natera/platform/lib/hooks";
import { Button } from "@natera/form";
import { Switch } from "@natera/material/lib/switch";
import SpinnerView from "@app/components/spinnerView";
import { Profile } from "@app/provider/profile";
import FactorList from "@app/components/factorList/factorList";
import { HeapEventLocation } from "@app/provider/types";
import { Notification, VerifyCodeFormLegacy } from "@app/components";
import { routes } from "@app/routing";
import { FactorsObject } from "@app/provider/mfaSetup";
import { setCodeVerificationErrors } from "@app/provider/mfaError";

import "./factorContainer.scss";
import { factorMessages as messages } from "./factorMessages";

interface Props {
  profile?: Profile;
}

const FactorContainer: React.FunctionComponent<Props> = ({ profile }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { loadProfile, isLoading } = React.useContext(UserContext);

  const [isActive, setIsActive] = React.useState(false);
  const [isForcedViaPhone, setIsForcedViaPhone] = React.useState(false);
  const [selectedFactor, setSelectedFactor] = React.useState<Factor>();
  const errorController = useErrorController();

  const { getProfileIsLoading } = React.useContext(ProfileContext);

  const {
    isLoading: isFactorLoading,
    enrolledFactor,
    isUserInMFAGroup,
    loadIsUserInMFAGroup,
    isFirstSetup,
    factorList,
    handleSkip,
    activateUserFactor,
    loadFactorList,
    resendUserFactorCode,
    handleFactorSetup,
    resetActivatedFactor,
  } = React.useContext(MfaSetupContext);

  useEffect(() => {
    const twoFaState = isUserInMFAGroup;
    setIsActive(twoFaState);
    if (isUserInMFAGroup) {
      loadProfile();
    }
  }, [isUserInMFAGroup]);

  useEffect(() => {
    loadFactorList();
    loadIsUserInMFAGroup();
    if (isFirstSetup) {
      setIsForcedViaPhone(true);
      setIsActive(true);
    } else {
      resetActivatedFactor();
    }
    return () => resetActivatedFactor();
  }, []);

  const setupFactor: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    handleFactorSetup(selectedFactor, isActive, isUserInMFAGroup);
  };

  const handleCode = async (code: string) => {
    try {
      const heapEventLocation = isFirstSetup
        ? HeapEventLocation.accountCreation
        : HeapEventLocation.patientProfile;
      await activateUserFactor(code, heapEventLocation);
    } catch (error) {
      setCodeVerificationErrors(errorController, intl);
    }
  };

  const handleSwitchClick = () => {
    setIsActive(!isActive);
    setSelectedFactor(undefined);
  };

  const handleCancel = () => {
    navigate(routes.securitySettings + location.search);
  };

  const handleUpdateContactDetails = () => {
    navigate(routes.contactDetails);
  };

  const prepareFactorsObject = () => {
    if (!factorList) return {};
    const factorObj: FactorsObject = {};
    factorList.forEach((factor) => (factorObj[factor.factorType] = factor));
    return factorObj;
  };

  const isSubmitActive = !!selectedFactor || (!isActive && isUserInMFAGroup);

  return (
    <>
      <SpinnerView isLoading={isLoading || isFactorLoading} />
      {enrolledFactor ? (
        <ErrorProvider controller={errorController}>
          <MfaErrorProvider>
            <VerifyCodeFormLegacy
              type={enrolledFactor.factorType}
              profile={enrolledFactor.profile}
              handleCode={handleCode}
              handleResend={resendUserFactorCode}
            />
          </MfaErrorProvider>
        </ErrorProvider>
      ) : (
        <div className="factor-container">
          {!isFirstSetup && (
            <h2>{intl.formatMessage(messages.factorListTitle)}</h2>
          )}
          <div className="factor-switch">
            <h3>{intl.formatMessage(messages.factorListSwitcher)}</h3>
            <Switch
              checked={isActive}
              disabled={isFirstSetup && isForcedViaPhone}
              id="factorSwitch"
              onClick={handleSwitchClick}
            />
          </div>

          <div className="factor-switch__information">
            <div>
              <p>{intl.formatMessage(messages.factorListDescription)}</p>
              <ul>
                <li>{intl.formatMessage(messages.factorListPassword)}</li>
                <li>{intl.formatMessage(messages.factorListCodeSent)}</li>
              </ul>
            </div>
          </div>

          <form onSubmit={setupFactor}>
            {isActive && (
              <>
                {!isFirstSetup && (
                  <>
                    <Notification
                      className="factor__info-notification"
                      type="info"
                      titleElem={intl.formatMessage(messages.factorListInfo)}
                    >
                      {intl.formatMessage(messages.factorListInfoMessage)}
                    </Notification>
                    <p className="factor__select-additional">
                      {intl.formatMessage(
                        messages.factorListSelectAdditionalMethod
                      )}
                    </p>
                  </>
                )}
                <FactorList
                  profile={profile}
                  factorsObject={prepareFactorsObject()}
                  displayEmail={!isForcedViaPhone}
                  setSelectedFactor={setSelectedFactor}
                />
              </>
            )}

            <div className="factor-footer">
              <Button unelevated type="submit" disabled={!isSubmitActive}>
                {isUserInMFAGroup
                  ? intl.formatMessage(messages.factorListUpdate)
                  : intl.formatMessage(messages.factorListSetUp)}
              </Button>
              {isFirstSetup && !isForcedViaPhone && (
                <Button
                  onClick={handleSkip}
                  disabled={getProfileIsLoading}
                  type="button"
                >
                  {intl.formatMessage(messages.factorListSkip)}
                </Button>
              )}
              {!isFirstSetup && (
                <>
                  <Button
                    onClick={handleUpdateContactDetails}
                    outlined
                    type="button"
                  >
                    {intl.formatMessage(
                      messages.factorListUpdateContactDetails
                    )}
                  </Button>
                  <Button
                    onClick={handleCancel}
                    disabled={getProfileIsLoading}
                    type="button"
                  >
                    {intl.formatMessage(messages.factorListCancel)}
                  </Button>
                </>
              )}
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default FactorContainer;
